// Please do not move the css imports from the top of this file.
import { ApolloProvider } from '@apollo/client';
import '@fastnorth/cerberus/dist/css/dashboard.css';
import { EventProvider } from '@fastnorth/react-results-event-components';
import '@fastnorth/react-results-event-components/index.css';
import { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { createBrowserRouter, createRoutesFromElements, redirect, Route, RouterProvider } from 'react-router-dom';
import { DEFAULT_URL_LOCALE } from './constants';
import { init } from './ddTooling';
import { buildGQLClient } from './graphql/apollo-client';
import i18n from './i18n';
import './index.scss';
import { getConsentCookieValue, loadGTMScript, setDefaultGTMConsent, updateConsentFromCookie } from './lib/googleTagManagerConsent';
import GalleryProvider from './provider/GalleryProvider';
import GroupsOverview from './views/gallery/components/groups-overview/GroupsOverview';
import SearchResultsSummary from './views/gallery/components/search/results/SearchResultsSummary';
import Loading from './views/loading/Loading';
init();

declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>;
  }
}

const initGTM = (): void => {
  window.dataLayer = window.dataLayer || [];
  setDefaultGTMConsent();
  updateConsentFromCookie();
  if (getConsentCookieValue()) {
    loadGTMScript();
  }
};
initGTM();

const GalleryOverview = lazy(() => import('./views/gallery/GalleryOverview'));
const App = lazy(() => import('./views/layout/App'));
const GalleryGroup = lazy(() => import('./views/gallery/components/group/GalleryGroup'));
const RouterError = lazy(() => import('./views/error/RouterError'));

const root = createRoot(document.getElementById('root') as HTMLElement);

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/">
      <Route
        path="galleries/:galleryId"
        loader={({ params }) => redirect(`/${DEFAULT_URL_LOCALE}/galleries/${params.galleryId}`)}
      />
      <Route
        path={':locale'}
        element={
          <Suspense fallback={<Loading busy={true} />}>
            <EventProvider>
              <GalleryProvider>
                <I18nextProvider i18n={i18n}>
                  <App />
                </I18nextProvider>
              </GalleryProvider>
            </EventProvider>
          </Suspense>
        }
        errorElement={
          <Suspense fallback={<Loading busy={true} />}>
            <RouterError />
          </Suspense>
        }>
        <Route
          path="galleries/:galleryId"
          element={
            <Suspense fallback={<Loading busy={true} />}>
              <GalleryOverview />
            </Suspense>
          }
          errorElement={
            <Suspense fallback={<Loading busy={true} />}>
              <RouterError />
            </Suspense>
          }>
          <Route
            index
            element={
              <Suspense fallback={<Loading busy={true} />}>
                <GroupsOverview />
              </Suspense>
            }
          />
          <Route
            path={'groups/:groupId'}
            element={
              <Suspense fallback={<Loading busy={true} />}>
                <GalleryGroup />
              </Suspense>
            }
            errorElement={
              <Suspense fallback={<Loading busy={true} />}>
                <RouterError />
              </Suspense>
            }
          />
          <Route
            path={'groups/:groupId/photos/:photoId'}
            element={
              <Suspense fallback={<Loading busy={true} />}>
                <GalleryGroup />
              </Suspense>
            }
            errorElement={
              <Suspense fallback={<Loading busy={true} />}>
                <RouterError />
              </Suspense>
            }
          />
          <Route
            path={'search'}
            element={
              <Suspense fallback={<Loading busy={true} />}>
                <SearchResultsSummary />
              </Suspense>
            }
          />
          <Route
            path={'search/:searchTag/photos/:photoId'}
            element={
              <Suspense fallback={<Loading busy={true} />}>
                <SearchResultsSummary />
              </Suspense>
            }
          />
        </Route>
      </Route>
    </Route>
  )
);

root.render(
  <ApolloProvider client={buildGQLClient()}>
    <RouterProvider router={router} />
  </ApolloProvider>
);
